import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusPill, LoadingBlock, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ListChecks, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";

import { useI18n } from "@/context/I18nContext";

const STATUS_STYLE: Record<string, string> = {
  open: "warn", in_progress: "info", blocked: "critical", completed: "good", cancelled: "inactive",
};

export default function Actions() {
  const {
    t
  } = useI18n();

  const { hasPerm } = useAuth();
  const canRun = hasPerm("mc.run");
  const [actions, setActions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/mc/actions${q}`).then((r) => setActions(r.data.actions)).finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const update = async (a: any, status: string) => {
    let reason: string | undefined;
    if (status === "cancelled") {
      reason = window.prompt("Reason for cancelling this action?") || undefined;
      if (!reason) return;
    }
    try {
      await api.patch(`/mc/actions/${a.id}`, { status, resolution_reason: reason });
      toast.success("Action updated");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    (<div>
      <PageHeader
        title="Persistent Actions"
        subtitle="Open items carry forward across shifts until resolved"
        testId="actions-header"
        actions={
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40" data-testid="actions-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("All statuses")}</SelectItem>
              <SelectItem value="open">{t("Open")}</SelectItem>
              <SelectItem value="in_progress">{t("In progress")}</SelectItem>
              <SelectItem value="blocked">{t("Blocked")}</SelectItem>
              <SelectItem value="completed">{t("Completed")}</SelectItem>
              <SelectItem value="cancelled">{t("Cancelled")}</SelectItem>
            </SelectContent>
          </Select>
        }
      />
      {loading ? <LoadingBlock /> : actions.length === 0 ? (
        <EmptyState icon={<ListChecks size={28} />} title="No actions" description="Actions created during the store day appear here." />
      ) : (
        <Card className="overflow-hidden" data-testid="actions-table">
          {actions.map((a) => (
            <div key={a.id} data-testid={`action-row-${a.id}`} className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 last:border-0">
              <div className="min-w-0">
                <div className="font-medium">{a.title}</div>
                <div className="text-xs text-muted-foreground capitalize">{stat(a.priority)}{" "}{t("priority · source:")}{" "}{a.source}{a.due_date ? ` · ${tl("due")} ${a.due_date}` : ""}</div>
                {a.resolution_reason && <div className="mt-0.5 text-xs italic text-muted-foreground">{a.resolution_reason}</div>}
              </div>
              <div className="flex items-center gap-2">
                <StatusPill status={STATUS_STYLE[a.status] || "info"}>{a.status.replace("_", " ")}</StatusPill>
                {canRun && !["completed", "cancelled"].includes(a.status) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" data-testid={`action-menu-${a.id}`}><MoreHorizontal size={16} /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => update(a, "in_progress")}>{t("Mark in progress")}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => update(a, "blocked")}>{t("Mark blocked")}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => update(a, "completed")}>{t("Complete")}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => update(a, "cancelled")} className="text-status-critical">{t("Cancel…")}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>)
  );
}
